/**
 * GuidesListView Component
 *
 * Displays a list of guides in a table format with real-time updates.
 *
 * Features:
 * - Display all guides in table format
 * - Show name, key, creation date
 * - Real-time updates when guides are created, updated, or deleted
 * - Action buttons: Edit, Clone, Delete
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Typography, useTheme, Stack, Menu, MenuItem, Button, Snackbar, Alert, alpha } from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DynamicTable, type TableColumn, SDialog } from '@modules/ui-components';
import type { GuideService } from '@modules/firebase-core/services/GuideService';
import type { Guide } from '@segmently/schema-models/auth';

// Unsubscribe function type
type Unsubscribe = () => void;

export interface GuidesListViewProps {
  /** Project ID for which to display guides */
  projectId: string;

  /** Guide service instance (dependency injection for testing) */
  guideService: GuideService;

  /** Optional: Called when Create button is clicked */
  onCreate?: () => void;

  /** Optional: Called when Edit button is clicked */
  onEdit?: (guide: Guide) => void;

  /** Optional: Called when Clone button is clicked */
  onClone?: (guide: Guide) => void;

  /** Optional: Called when Delete button is clicked */
  onDelete?: (guide: Guide) => void;
}

/**
 * Format ISO date string to two-line format
 * Line 1: MMM DD, YYYY
 * Line 2: HH:mm
 */
const formatDate = (isoString?: string): React.ReactNode => {
  if (!isoString) return 'N/A';

  try {
    const date = new Date(isoString);

    // Validate that the date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }

    // Format date line: Oct 29, 2025
    const dateLine = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

    // Format time line: 14:30 (use UTC to match test expectations)
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const timeLine = `${hours}:${minutes}`;

    return (
      <Stack spacing={0}>
        <Typography variant="body2">{dateLine}</Typography>
        <Typography variant="body2" color="text.secondary">{timeLine}</Typography>
      </Stack>
    );
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Truncate description to 500 characters with ellipsis
 */
const truncateText = (text?: string): string => {
  if (!text) return '';
  if (text.length <= 500) return text;
  return text.substring(0, 500) + '...';
};

export const GuidesListView: React.FC<GuidesListViewProps> = ({
  projectId,
  guideService,
  onCreate,
  onEdit,
  onClone,
  onDelete,
}) => {
  const theme = useTheme();
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Actions menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const menuOpen = Boolean(anchorEl);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [guideToDelete, setGuideToDelete] = useState<Guide | null>(null);

  // Toast notification state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Show toast notification
  const showToast = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Close toast notification
  const handleCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  // Open actions menu
  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>, guide: Guide) => {
    event.stopPropagation(); // Prevent row click
    setAnchorEl(event.currentTarget);
    setSelectedGuide(guide);
  }, []);

  // Close actions menu
  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setSelectedGuide(null);
  }, []);

  // Handle Edit menu item click
  const handleEditClick = useCallback(() => {
    if (!selectedGuide) return;

    handleCloseMenu();

    if (onEdit) {
      onEdit(selectedGuide);
    } else {
      console.log('[GuidesListView] Edit clicked:', selectedGuide.id);
    }
  }, [selectedGuide, onEdit, handleCloseMenu]);

  // Handle Edit button click (legacy - will be removed)
  const handleEdit = useCallback((guide: Guide) => {
    if (onEdit) {
      onEdit(guide);
    } else {
      console.log('[GuidesListView] Edit clicked:', guide.id);
    }
  }, [onEdit]);

  // Handle Clone menu item click
  const handleCloneClick = useCallback(async () => {
    if (!selectedGuide) return;

    handleCloseMenu();

    console.log('[GuidesListView] Clone button clicked, guide:', selectedGuide.id);

    if (onClone) {
      console.log('[GuidesListView] Using external onClone callback');
      onClone(selectedGuide);
      return;
    }

    // Guard: check guideService exists
    if (!guideService) {
      console.error('[GuidesListView] guideService is undefined!');
      alert('Error: Guide service not available');
      return;
    }

    // Guard: check guide has ID
    if (!selectedGuide.id) {
      console.error('[GuidesListView] Guide has no ID:', selectedGuide);
      alert('Error: Guide ID is missing');
      return;
    }

    console.log('[GuidesListView] Starting clone operation...');

    try {
      // Create a copy with modified name
      const clonedData: Partial<Guide> = {
        name: `${selectedGuide.name} (Copy)`,
        key: `${selectedGuide.key}_copy_${Date.now()}`,
        fullArticleLink: selectedGuide.fullArticleLink,
        content: selectedGuide.content ? [...selectedGuide.content] : undefined,
      };

      const cloned = await guideService.create(clonedData);
      console.log('[GuidesListView] ✅ Clone completed:', cloned.id);
      console.log('[GuidesListView] Cloned name:', cloned.name);

      // Show success toast
      showToast('Guide cloned successfully', 'success');

      // Real-time listener will automatically update the list
    } catch (error: any) {
      console.error('[GuidesListView] ❌ Clone failed:', error);
      console.error('[GuidesListView] Error message:', error?.message);
      console.error('[GuidesListView] Error stack:', error?.stack);

      // Show error toast
      showToast(`Failed to clone guide: ${error?.message || 'Unknown error'}`, 'error');
    }
  }, [selectedGuide, onClone, guideService, handleCloseMenu, showToast]);

  // Handle Create button click
  const handleCreateClick = useCallback(() => {
    if (onCreate) {
      onCreate();
    }
  }, [onCreate]);

  // Handle Delete menu item click
  const handleDeleteClick = useCallback(() => {
    if (!selectedGuide) return;

    handleCloseMenu();

    if (onDelete) {
      onDelete(selectedGuide);
      return;
    }

    // Show BaseDialog confirmation
    setGuideToDelete(selectedGuide);
    setDeleteDialogOpen(true);
  }, [selectedGuide, onDelete, handleCloseMenu]);

  // Confirm delete in dialog
  const handleConfirmDelete = useCallback(async () => {
    if (!guideToDelete) return;

    setDeleteDialogOpen(false);

    try {
      console.log('[GuidesListView] Deleting guide:', guideToDelete.id);
      await guideService.delete(guideToDelete.id!);
      console.log('[GuidesListView] Guide deleted successfully');

      // Show success toast
      showToast('Guide deleted successfully', 'success');

      // No need to update state - real-time listener will handle it
    } catch (error: any) {
      console.error('[GuidesListView] Delete failed:', error);

      // Show error toast
      showToast(`Failed to delete guide: ${error?.message || 'Please try again'}`, 'error');
    } finally {
      setGuideToDelete(null);
    }
  }, [guideToDelete, guideService, showToast]);

  // Cancel delete in dialog
  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setGuideToDelete(null);
  }, []);

  // Define table columns
  const columns: TableColumn<Guide>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      minWidth: 200,
      align: 'left',
    },
    {
      key: 'key',
      label: 'Key',
      sortable: true,
      minWidth: 150,
      align: 'left',
    },
    {
      key: 'fullArticleLink',
      label: 'Article Link',
      sortable: false,
      minWidth: 200,
      align: 'left',
      format: (value: string) => truncateText(value),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      minWidth: 180,
      align: 'left',
      format: (value: string) => formatDate(value),
    },
    {
      key: 'actions' as keyof Guide,
      label: 'Actions',
      minWidth: 80,
      align: 'center',
      format: (_value: any, row: Guide) => (
        <IconButton
          size="small"
          onClick={(e) => handleOpenMenu(e, row)}
          sx={{
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
          aria-label="Open actions menu"
          title="Actions"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Initial load: Fetch guides from Firestore
  useEffect(() => {
    let isMounted = true;

    const loadGuides = async () => {
      try {
        setLoading(true);
        const guides = await guideService.list({ limit: 50 });

        if (isMounted) {
          setGuides(guides);
          setLoading(false);
        }
      } catch (error) {
        console.error('[GuidesListView] Failed to load guides:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadGuides();

    return () => {
      isMounted = false;
    };
  }, [guideService]);

  // Real-time subscription: Listen to collection changes
  useEffect(() => {
    console.log('[GuidesListView] Setting up real-time listener via GuideService');

    // GuideService doesn't have onCollectionSnapshot, so we'll use list() with polling
    // or implement a custom listener if needed
    const intervalId = setInterval(async () => {
      try {
        const guides = await guideService.list({ limit: 50 });
        setGuides(guides);
        setLoading(false);
      } catch (error) {
        console.error('[GuidesListView] Failed to refresh guides:', error);
      }
    }, 5000); // Poll every 5 seconds

    console.log('[GuidesListView] Real-time listener setup complete');

    // Cleanup: Clear interval on unmount
    return () => {
      console.log('[GuidesListView] Clearing polling interval');
      clearInterval(intervalId);
    };
  }, [guideService]);

  // Empty state
  if (!loading && guides.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={400}
        gap={2}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(4),
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No guides found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first guide to get started
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateClick}
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none', mt: 2 }}
        >
          Create Guide
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* Create Guide button */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateClick}
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none' }}
        >
          Create Guide
        </Button>
      </Box>

      <DynamicTable
        data={guides}
        columns={columns}
        keyField="id"
        sortable={true}
        filterable={false}
        loading={loading}
        loadingRows={5}
        variant="striped"
        size="medium"
        maxRows={50}
        onRowClick={(guide) => handleEdit(guide)}
        sx={{
          backgroundColor: theme.palette.background.paper,
          maxHeight: 'none',
          '& .MuiTableHead-root': {
            '& .MuiTableCell-root': {
              backgroundColor: alpha(theme.palette.action.hover, 0.04),
              color: theme.palette.text.primary,
              fontWeight: theme.typography.fontWeightMedium,
            }
          },
          '& .MuiTableBody-root': {
            '& .MuiTableRow-root': {
              '&:nth-of-type(odd)': {
                backgroundColor: alpha(theme.palette.action.hover, 0.02),
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.08),
              },
              '& .MuiTableCell-root': {
                color: theme.palette.text.primary,
              }
            }
          }
        }}
      />

      {/* Actions dropdown menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <Typography variant="body2">Edit</Typography>
        </MenuItem>
        <MenuItem onClick={handleCloneClick}>
          <Typography variant="body2">Clone</Typography>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <Typography variant="body2">Delete</Typography>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <SDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        title="Delete Guide"
        primaryAction={{
          label: 'Delete',
          onClick: handleConfirmDelete,
          color: 'error',
          'aria-label': 'Confirm delete guide',
        }}
      >
        <Typography variant="body1">
          Are you sure you want to delete "<strong>{guideToDelete?.name}</strong>"?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This action cannot be undone.
        </Typography>
      </SDialog>

      {/* Toast notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

GuidesListView.displayName = 'GuidesListView';
